import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Empty, Input, message, Modal, Select, Spin, Typography } from 'antd';
import {
  useLazyGetOrganizationUnitsWithoutUserOrganizationUnitIdQuery,
  usePatchTransferSupportUnitMutation
} from '../../../services';
import { OrganizationUnitDto, TransferSupportUnit } from '../../../types';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';

const { Text } = Typography;
const { TextArea } = Input;

interface ModalTransferAnotherOrganizationUni {
  onActionSuccess: () => void;
  ticketId: number;
}

const ModalTransferAnotherOrganizationUnit: React.FC<ModalTransferAnotherOrganizationUni> = ({
  onActionSuccess,
  ticketId
}) => {
  const profile = useSelector((state: RootState) => state.users.userProfile);

  // Lazy API
  const [trigger, { data, isFetching, isLoading, isError, error }] =
    useLazyGetOrganizationUnitsWithoutUserOrganizationUnitIdQuery();

  // Dùng riêng cho đơn vị con
  const [triggerGetTeamId, { data: dataTeamId, isFetching: isFetchingTeam, isLoading: isLoadingTeam }] =
    useLazyGetOrganizationUnitsWithoutUserOrganizationUnitIdQuery();

  const [transferTicketToSupportUnit] = usePatchTransferSupportUnitMutation();

  // Local state
  const [selectedUnitId, setSelectedUnitId] = useState<number | null>(null); // Đơn vị phụ trợ (parent)
  const [selectedChildId, setSelectedChildId] = useState<number | null>(null); // Đơn vị con

  const [searchValue, setSearchValue] = useState<string>('');
  const [requestContent, setRequestContent] = useState<string>('');

  // Lấy đơn vị phụ trợ
  useEffect(() => {
    trigger({ isLoadAll: true, organizationUnitClassifyId: [9] }); // 9: đơn vị phụ trợ
  }, []);

  // khi chọn đơn vị phụ trợ -> gọi API lấy đơn vị con theo parentId
  useEffect(() => {
    if (selectedUnitId) {
      setSelectedChildId(null); // reset lựa chọn cũ nếu có
      triggerGetTeamId({ isLoadAll: true, parentId: [selectedUnitId] });
    }
  }, [selectedUnitId, triggerGetTeamId]);

  const units: OrganizationUnitDto[] = useMemo(() => {
    const rows = (data as any)?.data?.rows;
    return Array.isArray(rows) ? rows : [];
  }, [data]);
  const childUnits: OrganizationUnitDto[] = useMemo(() => {
    const rows = (dataTeamId as any)?.data?.rows;
    return Array.isArray(rows) ? rows : [];
  }, [dataTeamId]);

  const selectOptions = useMemo(
    () =>
      units.map((u) => ({
        value: u.organizationUnitId,
        label: [u.code, u.name].filter(Boolean).join(' - ')
      })),
    [units]
  );

  const childOptions = useMemo(
    () =>
      childUnits.map((u) => ({
        value: u.organizationUnitId,
        label: [u.code, u.name].filter(Boolean).join(' - ')
      })),
    [childUnits]
  );

  const loading = isFetching || isLoading;
  const loadingChild = isFetchingTeam || isLoadingTeam;

  const handleConfirm = () => {
    if (!selectedUnitId) {
      message.warning('Vui lòng chọn đơn vị xử lý');
      return;
    }
    if (!requestContent.trim()) {
      message.warning('Vui lòng nhập nội dung phản hồi');
      return;
    }
    if (!selectedChildId) {
      message.warning('Vui lòng chọn đơn vị con');
      return;
    }

    const payload: TransferSupportUnit = {
      fromUnitId:
        profile &&
        profile.organizationUnit &&
        profile.organizationUnit.parent &&
        profile.organizationUnit.parent.organizationUnitId
          ? profile.organizationUnit.parent.organizationUnitId
          : 0,
      fromTeamId: profile ? profile?.organizationUnitId : 0,

      toUnitId: selectedUnitId,
      toTeamId: selectedChildId,

      reason: requestContent.trim(),
      ticketId
    };

    console.log('payload: ', payload);

    Modal.confirm({
      title: 'Xác nhận chuyển phiếu đến đơn vị hỗ trợ',
      okText: 'Xác nhận',
      cancelText: 'Hủy',
      onOk() {
        if (ticketId) {
          transferTicketToSupportUnit(payload)
            .unwrap()
            .then(() => {
              onActionSuccess?.();
              message.success(`Chuyển phiếu thành công`);
            })
            .catch((err) => {
              console.log(err);
              message.error('Chuyển phiếu thất bại, vui lòng thử lại sau');
            });
        }
      }
    });
  };

  return (
    <Card className='confirm-modal mx-auto bg-[#F5F5F7] p-4'>
      <div className='mb-3 text-center'>
        <Typography.Text className='text-0.5xl font-semibold uppercase'>Chuyển đơn vị xử lý</Typography.Text>
      </div>

      {loading && (
        <div className='my-6 text-center'>
          <Spin />
        </div>
      )}

      {isError && (
        <Text type='danger'>
          Lỗi tải danh sách: {String((error as any)?.data?.message || (error as any)?.error || 'Unknown error')}
        </Text>
      )}

      {!loading && !isError && (
        <section>
          {/* Select đơn vị */}
          <section className='mt-4'>
            <Select<number>
              className='w-full'
              placeholder='Chọn đơn vị'
              showSearch
              optionFilterProp='label'
              onSearch={setSearchValue}
              searchValue={searchValue}
              onChange={(val) => setSelectedUnitId(val)}
              options={selectOptions}
              value={selectedUnitId ?? undefined}
              allowClear
              loading={loading}
              notFoundContent={<Empty description='Không có dữ liệu' image={Empty.PRESENTED_IMAGE_SIMPLE} />}
              aria-label='Chọn đơn vị xử lý'
              size='middle'
              getPopupContainer={(triggerNode) => triggerNode.parentElement!}
            />

            {/* Select đơn vị con */}
            <section className='mt-3'>
              <Select<number>
                className='w-full'
                placeholder='Chọn đơn vị con'
                showSearch
                optionFilterProp='label'
                onChange={(val) => setSelectedChildId(val)}
                options={childOptions}
                value={selectedChildId ?? undefined}
                disabled={!selectedUnitId}
                loading={loadingChild}
                notFoundContent={<Empty description='Không có dữ liệu' image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                aria-label='Chọn đơn vị con'
                size='middle'
                getPopupContainer={(triggerNode) => triggerNode.parentElement!}
              />
            </section>
          </section>

          {/* TextArea nhập nội dung */}
          <section className='mt-4 space-y-1 text-left'>
            <Text className='text-[14px]/[16px] font-semibold'>Nội dung phản hồi</Text>
            <TextArea
              placeholder='Nhập nội dung phản hồi'
              value={requestContent}
              onChange={(e) => setRequestContent(e.target.value)}
              autoSize={{ minRows: 10, maxRows: 20 }}
              className='rounded-lg bg-[#F7F8F6]'
              aria-label='Nội dung phản hồi'
            />
          </section>

          <section className='mt-4 text-right'>
            <Button type='primary' onClick={handleConfirm} disabled={!selectedUnitId || !requestContent.trim()}>
              Xác nhận
            </Button>
          </section>
        </section>
      )}
    </Card>
  );
};

export default ModalTransferAnotherOrganizationUnit;
